import { Box, Divider, Grid, TextField } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import { SubmitButton } from '../common/form/SubmitButton';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSnackbar } from '../../hooks/useAppSnackbar';
import { useAppSelector } from '../../store';
import { selectIdentityCompanyId } from '../../store/entities/identity';
import { Loader } from '../common/Loader';
import { FormContainer } from '../common/form/FormContainer';
import { LanguageSelectWithoutFormik } from '../common/form/LanguageSelectWithoutFormik';
import { SUPPORTED_LANGUAGES } from '../../utils/i18n';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import { HtmlEditor } from '../common/html/HtmlEditor';
import { QcsButton } from '../common/basic/QcsButton';
import AttachmentForm from '../qcsCompanySettings/AttachmentForm';
import { QcsTable } from '../common/basic/QcsTable';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { QcsTableRow } from '../common/basic/QcsTableRow';
import { QcsTableHead } from '../common/basic/QcsTableHead';
import { QcsTableBody } from '../common/basic/QcsTableBody';
import { DatePicker } from '../common/form/DatePicker';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { QcsTimePicker } from '../common/form/QcsTimePicker';
import { companySettingsService, workplaceHandoverService } from '@api/services/services';
import { UpdateWorkplaceHandoverSettingsModel } from '@api/models/settings/UpdateWorkplaceHandoverSettingsModel';
import { WorkplaceHandoverSectionGroupDto } from '@api/models/settings/WorkplaceHandoverSectionGroupDto';
import { EmployeeAutocomplete } from '../common/form/EmployeeAutocomplete';
import { ReferenceDto } from '@api/models/employees/ReferenceDto';
import { getFirstLastNameObj } from '../../utils/format';
import { UserRole } from '@api/models/enums/UserRole';

interface HandoverFormValues extends UpdateWorkplaceHandoverSettingsModel {
  bozpManager: ReferenceDto | null;
}

export const CompSettTabHandover: FC = () => {
  const { t } = useTranslation();
  const identityCompanyId = useAppSelector(selectIdentityCompanyId);
  const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useAppSnackbar();
  const [initialValues, setInitialValues] = useState<HandoverFormValues>();
  const [sections, setSections] = useState<WorkplaceHandoverSectionGroupDto[]>([]);
  const [loading, setLoading] = useState(true);
  const companyCustomization = useAppSelector(selectCompanyCustomization);
  const [language, setLanguage] = useState<string | undefined>(companyCustomization?.applicationLanguages[0]);
  const [showAttachmentForm, setShowAttachmentForm] = useState(false);
  const [attachmentEdit, setAttachmentEdit] = useState<WorkplaceHandoverSectionGroupDto>();

  const getHtmlRef = useRef<(() => string) | null>(null);
  const reloadHtmlRef = useRef<(() => void) | null>(null);

  const updateTextContentForLanguage = useCallback((textContent: any[], language: string | undefined, newContent: Partial<any>) => {
    return textContent?.map((item) => {
      if (item.language === language) {
        return { ...item, ...newContent };
      }
      return item;
    });
  }, []);

  const deleteSection = async (sectionId: string) => {
    setSections((prevSections) => prevSections.filter((section) => section.id !== sectionId));
    await companySettingsService.updateWorkplaceHandoverSections(
      sections
        .filter((section) => section.id !== sectionId)
        .map((section) => ({
          ...section,
          order: section.order,
          sections: section.sections.map((item) => ({
            ...item,
            id: item.id || '',
          })),
        })),
      { silentSuccess: true },
    );
    enqueueSuccessSnackbar(t('handoverSettings.sectionDeleted'));
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [settingsRes, sections] = await Promise.all([
          companySettingsService.getWorkplaceHandoverSettings(),
          companySettingsService.getWorkplaceHandoverSections(),
        ]);
        setInitialValues({
          ...settingsRes.data,
          bozpManagerId: settingsRes.data.bozpManager?.id ?? null,
          bozpManager: settingsRes.data.bozpManager
            ? { id: settingsRes.data.bozpManager.id!, name: getFirstLastNameObj(settingsRes.data.bozpManager) }
            : null,
          creationDate: settingsRes.data.creationDate ? new Date(settingsRes.data.creationDate).toISOString() : new Date().toISOString(),
          revisionDate: settingsRes.data.revisionDate ? new Date(settingsRes.data.revisionDate).toISOString() : new Date().toISOString(),
          textContent:
            settingsRes.data.textContent?.map((item) => ({
              ...item,
              content: item.language === language ? item.content : '',
              title: item.language === language ? (item.title ?? '') : '',
            })) || [],
        });
        setSections(sections.data || []);
      } catch (err) {
        console.error(err);
        enqueueErrorSnackbar(t('errors.generic'));
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityCompanyId]);

  if (loading || !initialValues) return <Loader />;

  return (
    <>
      {showAttachmentForm && (
        <AttachmentForm
          companyId={identityCompanyId}
          onClose={() => {
            setShowAttachmentForm(false);
            setAttachmentEdit(undefined);
            companySettingsService.getWorkplaceHandoverSections().then((res) => {
              setSections(res.data || []);
            });
          }}
          attachment={attachmentEdit}
        />
      )}
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={async (values) => {
          const currTextContent = getHtmlRef.current?.();
          const newTextContent = updateTextContentForLanguage(values.textContent ?? [], language, { content: currTextContent });

          await companySettingsService.updateWorkplaceHandoverSettings({
            ...values,
            bozpManagerId: values.bozpManager?.id ?? null,
            textContent: newTextContent,
          });
        }}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <FormContainer>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Field
                    label={t('handoverSettings.nameOfProtocol')}
                    component={TextField}
                    fullWidth
                    sx={{ backgroundColor: (theme: any) => theme.palette.common.white }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue('textContent', updateTextContentForLanguage(values.textContent ?? [], language, { title: e.target.value }));
                    }}
                    value={values.textContent?.find((item) => item.language === language)?.title}
                  />
                </Grid>
                <Grid size={6}>
                  <LanguageSelectWithoutFormik
                    fullWidth
                    languages={SUPPORTED_LANGUAGES}
                    allowedLanguages={companyCustomization?.applicationLanguages}
                    value={language}
                    label={t('settings.companyPages.language')}
                    onChange={(e) => {
                      setLanguage(e.target.value);
                      const currentTextContent = values.textContent;
                      const currTextContent = getHtmlRef.current?.();
                      const newTextContent = currentTextContent?.map((item) => {
                        if (item.language === language) {
                          return { ...item, content: currTextContent };
                        }
                        return item;
                      });
                      setFieldValue('textContent', newTextContent);
                      requestAnimationFrame(() => {
                        reloadHtmlRef.current?.();
                      });
                    }}
                  />
                </Grid>
                <Grid size={3}>
                  <DatePicker label={t('handoverSettings.creationDate')} name="creationDate" css={{ backgroundColor: '#fff', width: '100%' }} />
                </Grid>
                <Grid size={3}>
                  <DatePicker label={t('handoverSettings.revisionDate')} name="revisionDate" css={{ backgroundColor: '#fff', width: '100%' }} />
                </Grid>
                <Grid size={6}>
                  <EmployeeAutocomplete
                    name="bozpManager"
                    label={t('handoverSettings.HSEManager')}
                    allowedRoles={[UserRole.ManagerOsah, UserRole.ManagerWorkplace]}
                  />
                </Grid>
                <Grid size={6} flexDirection="row" display="flex" gap={2}>
                  <QcsTimePicker label={t('handoverSettings.workingHoursFrom')} name="workingTimeFrom" />
                  <QcsTimePicker label={t('handoverSettings.workingHoursTo')} name="workingTimeTo" />
                </Grid>
                <Grid size={6}></Grid>
                <Grid size={12}>
                  <HtmlEditor
                    html={values.textContent?.find((item) => item.language === language)?.content}
                    getHtmlRef={getHtmlRef}
                    reloadHtmlRef={reloadHtmlRef}
                    allowSelectRisks={false}
                  />
                </Grid>
                <Grid size={12}>
                  <Box mb={4} mt={2}>
                    <SubmitButton text={t('common.save')} />
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <QcsButton onClick={() => setShowAttachmentForm(true)} variant="outlined" color="primary" sx={{ width: 300 }}>
                    {t('handoverSettings.addSection')}
                  </QcsButton>
                  {sections?.length > 0 && (
                    <QcsTable css={{ marginTop: '20px', backgroundColor: '#fff' }}>
                      <QcsTableHead>
                        <QcsTableRow>
                          <QcsTableCell>{t('handoverSettings.sectionTitle')}</QcsTableCell>
                          <QcsTableCell> </QcsTableCell>
                        </QcsTableRow>
                      </QcsTableHead>
                      <QcsTableBody>
                        {sections.map((section, idx) => (
                          <QcsTableRow key={section.id}>
                            <QcsTableCell>{section.sections.find((item) => item.language === language)?.title || ' - '}</QcsTableCell>
                            <QcsTableCell align="right">
                              <QcsButton
                                variant="outlined"
                                sx={{ mr: 1 }}
                                color="primary"
                                onClick={() => {
                                  setAttachmentEdit(section);
                                  setShowAttachmentForm(true);
                                }}
                              >
                                {t('handoverSettings.editSection')}
                              </QcsButton>
                              <QcsButton
                                variant="outlined"
                                color="error"
                                onClick={() => {
                                  deleteSection(section.id || '');
                                }}
                              >
                                {t('handoverSettings.deleteSection')}
                              </QcsButton>
                              {/* Move Up */}
                              <QcsButton
                                variant="text"
                                color="primary"
                                sx={{ minWidth: 0, p: 1, ml: 2 }}
                                disabled={idx === 0}
                                onClick={async () => {
                                  if (idx === 0) return;
                                  const newSections = [...sections];
                                  [newSections[idx - 1], newSections[idx]] = [newSections[idx], newSections[idx - 1]];
                                  // Update order property if needed
                                  newSections.forEach((s, i) => (s.order = i));
                                  setSections(newSections);
                                  await companySettingsService.updateWorkplaceHandoverSections(
                                    newSections.map((section) => ({
                                      ...section,
                                      order: section.order,
                                      sections: section.sections.map((item) => ({
                                        ...item,
                                        id: item.id || '',
                                      })),
                                    })),
                                  );
                                }}
                              >
                                <ArrowUpward />
                              </QcsButton>
                              {/* Move Down */}
                              <QcsButton
                                variant="text"
                                color="primary"
                                sx={{ minWidth: 0, p: 1 }}
                                disabled={idx === sections.length - 1}
                                onClick={async () => {
                                  if (idx === sections.length - 1) return;
                                  const newSections = [...sections];
                                  [newSections[idx], newSections[idx + 1]] = [newSections[idx + 1], newSections[idx]];
                                  // Update order property if needed
                                  newSections.forEach((s, i) => (s.order = i));
                                  setSections(newSections);
                                  await companySettingsService.updateWorkplaceHandoverSections(
                                    newSections.map((section) => ({
                                      ...section,
                                      order: section.order,
                                      sections: section.sections.map((item) => ({
                                        ...item,
                                        id: item.id || '',
                                      })),
                                    })),
                                  );
                                }}
                              >
                                <ArrowDownward />
                              </QcsButton>
                            </QcsTableCell>
                          </QcsTableRow>
                        ))}
                      </QcsTableBody>
                    </QcsTable>
                  )}
                  <Box sx={{ mb: 2 }} />

                  {/* Preview of protocol PDF */}
                  <QcsButton
                    variant="outlined"
                    sx={{ mt: 2, width: 300 }}
                    onClick={async () => {
                      const response = await workplaceHandoverService.previewWorkplaceHandover();
                      try {
                        const url = window.URL.createObjectURL(response.data);
                        window.open(url, '_blank');
                        // Optionally, revokeObjectURL after some time if desired
                        // setTimeout(() => window.URL.revokeObjectURL(url), 10000);
                      } catch (err) {
                        console.error(err);
                        enqueueErrorSnackbar(t('errors.generic'));
                      }
                    }}
                  >
                    {t('handoverSettings.previewOfProtocol')}
                  </QcsButton>
                </Grid>
              </Grid>
            </FormContainer>
          </Form>
        )}
      </Formik>
    </>
  );
};
